/**
 * Bhajan meanings — the pure layer.
 *
 * A MEANING node (nodeTypeId 10) holds up to ten independent translations of
 * one bhajan's meaning, one per language, in `<code>Text` fields:
 * `teText`, `enText`, `knText`, … (jnana-native/src/init/db/graph/nodeTypes/meaning.ts).
 * There is no single canonical text and no fallback chain in the data — a
 * meaning authored only in Telugu simply has no `enText` key.
 *
 * That is the normal case, not the edge case. Measured against the prod
 * catalog on 2026-07-29: of 728 meaning nodes, 637 have Telugu, 247 have
 * English, 10 Kannada, 1 Hindi, and none at all in the remaining six
 * languages. So an English reader opening a bhajan with a meaning finds no
 * English text roughly two times in three. Everything here is built around
 * that: the reader is offered the languages that exist, defaulting as close to
 * their own as the content allows, rather than being told "not available".
 */

import { LANGUAGE_BITMASK_ORDER } from '../utils/languages';
import type { AnyGraphNode } from '../types/graphNodes';

/** The bhajan → meaning edge. EdgeCardinality.Single in both graph mirrors. */
export const MEANING_EDGE = 'meaning';

/**
 * Language codes in the order a meaning's languages are offered.
 *
 * This is `LANGUAGE_BITMASK_ORDER` — Telugu first, English second — and that is
 * deliberate, not a leak of an unrelated constant. The app's picker iterates
 * `Object.keys(allLanguages)` (jnana-native/src/bmScreens/meanings/view/index.tsx:31),
 * whose declaration order is exactly this one, so listing Telugu above English
 * matches what a reader sees in the app. It is also the honest order for this
 * content: Telugu is the language most meanings are actually written in.
 */
export const MEANING_LANGUAGE_ORDER: readonly string[] = LANGUAGE_BITMASK_ORDER;

/** The `data` of a MEANING node as the API returns it UNTRIMMED (all languages). */
export type MeaningData = Record<string, unknown>;

/** The per-language field holding a meaning's text, e.g. 'te' → 'teText'. */
export const meaningTextField = (languageCode: string): string => `${languageCode}Text`;

/**
 * This bhajan's meaning node id, or null.
 *
 * Readable from the slim catalog index alone: the index keeps every node's
 * edges, so "does this bhajan have a meaning" costs no request. It strips
 * MEANING `data` to nothing, though, so the text always needs a second fetch —
 * see services/meaningsApi.
 */
export function meaningNodeIdFor(bhajan: AnyGraphNode | null | undefined): number | null {
  const edge = bhajan?.edges?.find((e) => e.name === MEANING_EDGE);
  if (!edge) return null;
  /**
   * A real, positive, integral number or nothing.
   *
   * `edges` comes from untyped API JSON, and this value is interpolated into a
   * request path (`/api/graph/nodes/${id}`). The type says `number`, but the
   * type is an assertion about the API, not a check — a string, a float or a
   * NaN would all sail through `> 0` (or, for a string, through `>` after
   * coercion) and reach the URL. A 0 in particular is not hypothetical: the
   * app's overflow menu coerces a missing id to `?? 0` and navigates with it
   * (useBhajanMenuConfig.ts:116).
   */
  const id = edge.nodeId;
  return typeof id === 'number' && Number.isSafeInteger(id) && id > 0 ? id : null;
}

/**
 * The text of a meaning in one language, or null when there is none.
 *
 * Trimmed, and whitespace-only counts as none.
 *
 * DIVERGENCE FROM THE APP, deliberate. Native has two helpers that disagree
 * here on purpose (pinned by a comment in
 * jnana-native/src/bmScreens/meanings/view/__tests__/meaningHelpers.test.ts:163):
 * `getAvailableMeaningLanguages` excludes a whitespace-only translation while
 * `getMeaningText` returns `''` for it. The visible consequence in the app is a
 * menu that offers "View Meaning" and then a screen that says the meaning is
 * only available in other languages. One rule in one place cannot produce that,
 * so this returns null for both, and `availableMeaningLanguages` is derived
 * from this function rather than repeating the test.
 */
export function meaningTextFor(
  data: MeaningData | null | undefined,
  languageCode: string
): string | null {
  const raw = data?.[meaningTextField(languageCode)];
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  return hasVisibleText(trimmed) ? trimmed : null;
}

/**
 * Zero-width characters, which `String.trim` does not remove.
 *
 * U+200B is all over this content as a stanza spacer, so a translation can
 * consist of nothing but a few of them — the author opened the field, pressed
 * enter, and saved. `'​'.trim()` is truthy, so a naive emptiness check calls that
 * a translation, lists the language as available, and renders a "Meaning"
 * heading over a blank panel. Worse, when it is the reader's OWN language it
 * wins the language pick, so no note explains the blank and a real translation
 * in another language is never offered.
 */
const ZERO_WIDTH = /[​-‍﻿]/g;

/** Does this text contain anything a reader could actually see? */
export const hasVisibleText = (text: string): boolean =>
  text.replace(ZERO_WIDTH, '').trim().length > 0;

/**
 * Every language this meaning is actually readable in, in MEANING_LANGUAGE_ORDER.
 *
 * Driven by the keys present on the node, never by the app's supported-language
 * list: six of the ten languages have no meaning content anywhere in the
 * catalog, and offering them would be ten tabs where four have text.
 */
export function availableMeaningLanguages(data: MeaningData | null | undefined): string[] {
  if (!data) return [];
  return MEANING_LANGUAGE_ORDER.filter((code) => meaningTextFor(data, code) !== null);
}

/** Why the reader is looking at the language they are looking at. */
export type MeaningLanguageChoice =
  /** Their own language had a meaning. Nothing to explain. */
  | { code: string; reason: 'preferred' }
  /** Their language had none, so English stood in. */
  | { code: string; reason: 'english-fallback' }
  /** Neither their language nor English. First available, in offering order. */
  | { code: string; reason: 'only-available' }
  /** The meaning has no readable text in any language. */
  | { code: null; reason: 'none' };

const ENGLISH = 'en';

/**
 * Which language to show, given what exists and what the reader reads.
 *
 * The app dead-ends here: it opens on the reader's own language and, when
 * there is no text in it, renders "The meaning is available in other
 * languages. Select one of the languages above." over an empty screen
 * (jnana-native/src/bmScreens/meanings/view/index.tsx:167). Since that is the
 * outcome for two thirds of English readers, this resolves to something
 * readable instead and says which language it landed on. The reader can still
 * change it; they are just not required to.
 *
 * English is preferred over "first available" as the stand-in because it is
 * the one language a reader of this site is known to have in common with the
 * content — the UI is English-only, so anyone here reads it.
 *
 * `available` is trusted to be in offering order (it comes from
 * availableMeaningLanguages); it is not re-sorted, so an explicitly ordered
 * list passed by a caller is honoured.
 */
export function pickMeaningLanguage(
  available: readonly string[],
  preferredCode: string | null | undefined
): MeaningLanguageChoice {
  if (available.length === 0) return { code: null, reason: 'none' };
  if (preferredCode && available.includes(preferredCode)) {
    return { code: preferredCode, reason: 'preferred' };
  }
  if (available.includes(ENGLISH)) return { code: ENGLISH, reason: 'english-fallback' };
  return { code: available[0], reason: 'only-available' };
}
